import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';

import FormControl from '@material-ui/core/FormControl';
import InputBase from '@material-ui/core/InputBase/InputBase';
import InputAdornment from '@material-ui/core/InputAdornment';

import { makeStyles, useTheme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  inputContainer: {
    width: '100%',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.secondary.light : '#f8f8f8'
  },
  textareaContainer: {
    width: '100%',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    outline: 'none',
    marginBottom: '8px',
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.secondary.light : '#f8f8f8'
  },
  inputColor: {
    width: '100%',
    background: theme.palette.background.paper,
    height: 40,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    outline: 'none',
    marginBottom: '8px',
    '&:active': {
      outline: 'none',
      borderRadius: '8px',
    },
    '&:focus': {
      outline: 'none !important',
      borderRadius: '8px',
    },
    '&:before': {
      outline: 'none',
      borderRadius: '8px',
    }
  },
  inputContainerBorderless: {
    width: '100%',
    background: theme.palette.background.paper,
    borderRadius: '8px',
    border: '0',
    outline: 'none',
    height: '45px',
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.secondary.light : '#f8f8f8'
  },
  input: {
    width: '100%',
    height: '40px',
    outline: 'none',
    paddingLeft: '16px',
  },
  textarea: {
    width: '100%',
    outline: 'none',
    padding: '8px 12px',
    lineHeight: '1.5rem',
  },
  search: {
    width: '240px',
    height: '40px',
    outline: 'none',
    border: `1.5px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    paddingLeft: '16px',
    paddingRight: '16px',
    fontWeight: 'normal !important',
  },
  startAdornment: {
    color: theme.palette.text.disabled,
  },
  endAdornment: {
    color: theme.palette.text.disabled,
  }
}));

const TextInput = (props) => {
  const {
    value,
    disabled = false,
    borderless = false,
    placeholder,
    onChange = () => {},
    startAdornment = '',
    endAdornment = '',
    width,
    type = 'text',
    multiline = false,
    maxLength,
    error = false,
    warning = false,
    rows = 1,
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const textColor = theme.palette.text.primary;
  const placeholderColor = theme.palette.text.disabled;
  const style = {
    backgroundColor: disabled
      ? theme.palette.action.disabledBackground
      : (theme.palette.type === 'dark' ? theme.palette.secondary.light : '#f8f8f8')
  };

  return <FormControl
    className={borderless ? classes.inputContainerBorderless : classes.inputContainer}
    style={error ? { ...style, borderColor: '#F53434' } : (warning ? { ...style, borderColor: '#F58634' } : style)}
  >
    <InputBase
      id={`text_${uuid()}`}
      type={type}
      className={multiline ? classes.textarea : classes.input}
      style={{ color: value ? textColor : placeholderColor }}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      disabled={disabled}
      width={width}
      multiline={multiline}
      rows={rows}
      startAdornment={
        startAdornment && (
          <InputAdornment position='start'>
                <span className={classes.startAdornment}>
                  {startAdornment}
                </span>
          </InputAdornment>
        )
      }
      endAdornment={
        endAdornment && (
          <InputAdornment position="end">
                <span className={classes.endAdornment}>
                  {endAdornment}
                </span>
          </InputAdornment>
        )
      }
      inputProps={maxLength ? { maxLength } : {}}
    />
  </FormControl>;
};

TextInput.propTypes = {
  placeholder: PropTypes.string,
  handleChange: PropTypes.func,
  handleClickShowPassword: PropTypes.func,
  handleMouseDownPassword: PropTypes.func,
  showPassword: PropTypes.bool,
  width: PropTypes.number,
  multiline: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default React.memo(TextInput);
